/**
 * Adhésion Black Dragon distincte du statut membre Shotokan.
 * Condition : membership Shotokan ACTIVE avant activation BD.
 */
import { BLACK_DRAGON_STATUSES, BD_CONTENT_VISIBILITY, MEMBERSHIP_STATUSES, TEMP_BD_APPROVER_POLICY } from "./membershipConstants.js";
import { isActiveMember } from "./membershipEngine.js";

export function canRequestBlackDragon(membership) {
  return isActiveMember(membership);
}

export function canInviteToBlackDragon(membership) {
  return Boolean(membership) && membership.status !== MEMBERSHIP_STATUSES.CANCELLED;
}

export function blackDragonGateMessage(membership, locale = "fr") {
  if (isActiveMember(membership)) return "";
  return locale === "en"
    ? "This person must first become a Shotokan AAMC member."
    : "Cette personne doit d'abord devenir membre de Shotokan AAMC.";
}

export function canApproveBlackDragon(viewerRole, settings = {}) {
  const roles = settings.blackDragonApproverRoles || ["hanshi", "admin"];
  return roles.includes(viewerRole);
}

export function bdApproverPolicy() {
  return TEMP_BD_APPROVER_POLICY;
}

/**
 * @param {object} membership
 * @param {'request'|'invite'} channel
 * @param {object} actor
 */
export function submitBlackDragonIntent(membership, channel, actor) {
  if (channel === "request" && !canRequestBlackDragon(membership)) {
    return { ok: false, code: "membership_required", membership };
  }
  const now = new Date().toISOString();
  const nextStatus = channel === "invite" ? BLACK_DRAGON_STATUSES.INVITED : BLACK_DRAGON_STATUSES.REQUEST_SUBMITTED;
  const historyEntry = {
    at: now,
    action: channel === "invite" ? "invited" : "request_submitted",
    actorUsername: actor?.username || "system",
    actorRole: actor?.role || "system",
  };
  return {
    ok: true,
    membership: {
      ...membership,
      blackDragon: {
        ...(membership.blackDragon || {}),
        status: nextStatus,
        channel,
        submittedAt: now,
        history: [...(membership.blackDragon?.history || []), historyEntry],
      },
    },
  };
}

export function decideBlackDragon(membership, decision, actor) {
  const now = new Date().toISOString();
  if (decision === "approve") {
    if (!isActiveMember(membership)) {
      return { ok: false, code: "membership_required", membership };
    }
    return {
      ok: true,
      membership: {
        ...membership,
        blackDragon: {
          ...(membership.blackDragon || {}),
          status: BLACK_DRAGON_STATUSES.ACTIVE,
          decidedAt: now,
          decidedBy: actor?.username || "admin",
          history: [
            ...(membership.blackDragon?.history || []),
            { at: now, action: "approved", actorUsername: actor?.username, actorRole: actor?.role },
          ],
        },
      },
    };
  }
  const refused = decision === "refuse";
  return {
    ok: true,
    membership: {
      ...membership,
      blackDragon: {
        ...(membership.blackDragon || {}),
        status: refused ? BLACK_DRAGON_STATUSES.REFUSED : BLACK_DRAGON_STATUSES.WITHDRAWN,
        decidedAt: now,
        decidedBy: actor?.username || "admin",
        history: [
          ...((membership.blackDragon || {}).history || []),
          {
            at: now,
            action: refused ? "refused" : "withdrawn",
            actorUsername: actor?.username,
            actorRole: actor?.role,
          },
        ],
      },
    },
  };
}

export function isBlackDragonActive(membership) {
  return membership?.blackDragon?.status === BLACK_DRAGON_STATUSES.ACTIVE;
}

/**
 * @param {object} content { visibility }
 * @param {object} viewer { role, isAuthenticated, isActiveMember, isBlackDragon }
 */
export function canViewBlackDragonContent(content, viewer) {
  const vis = content?.visibility || BD_CONTENT_VISIBILITY.PUBLIC;
  if (vis === BD_CONTENT_VISIBILITY.PUBLIC) return true;
  if (vis === BD_CONTENT_VISIBILITY.ADMIN) {
    return viewer?.role === "hanshi" || viewer?.role === "admin";
  }
  if (vis === BD_CONTENT_VISIBILITY.SHOTOKAN_MEMBERS) {
    return Boolean(viewer?.isActiveMember || viewer?.role === "hanshi" || viewer?.role === "admin");
  }
  if (vis === BD_CONTENT_VISIBILITY.BLACK_DRAGON) {
    return Boolean(viewer?.isBlackDragon || viewer?.role === "hanshi" || viewer?.role === "admin");
  }
  return false;
}
